import React, { useState, useEffect, useCallback, useRef } from 'react';
import EventViewSwitch from './EventViewSwitch';
import useEventViewMode from '../hooks/useEventViewMode';
import featuredWallService from '../services/featuredWallService';
import { getWallCache, saveWallList } from '../utils/wallCache';
import useMediaQuery from '../utils/useMediaQuery';
import WallEventList from './wall/WallEventList';
import { prepareEventPosters } from './wall/preparePosters';

/**
 * ALL EVENTS — the ranked list at the bottom of the events wall (events-wall handoff §5).
 *
 * Header: ALL EVENTS (n) · 50-MI RADIUS (≥ 640) · a 2px rule · the GRID / LIST switch. The body
 * is borderless WallEventCards in masonry columns — two on desktop, one below 980 or in LIST.
 *
 * The filters moved up into the page's FILTERS drawer (`useWallFilters`, passed in as
 * `filters`); this section only fetches with them. Fetching, pagination, the infinite-scroll
 * sentinel and the back-navigation restore are unchanged: page 0 per filter set, 24 at a time,
 * the whole loaded list cached so a return rebuilds it on the first paint.
 */
const PAGE = 24;

export default function AllEventsGrid({ filters, loading = false, location = null, restoreFromBack = false, onEventOpen = null, portalFor = null }) {
  // On back/forward, read the locally cached list ONCE and seed the list from it.
  const backCacheRef = useRef(restoreFromBack ? getWallCache() : null);
  const backCache = backCacheRef.current;
  const hasBackList = !!(backCache && Array.isArray(backCache.events) && backCache.events.length);

  // GRID / LIST — presentation only, persisted per user. Never refetches.
  const [viewMode, setViewMode] = useEventViewMode('grid');
  const phone = useMediaQuery('(max-width: 639px)');

  const { fundingFilter, category, selectedCities, selectedDays, search } = filters;

  const [fetched, setFetched] = useState(() => (hasBackList ? backCache.events : []));
  const [skip, setSkip] = useState(() => (hasBackList ? (backCache.skip || backCache.events.length) : 0));
  const [hasMore, setHasMore] = useState(() => (hasBackList ? backCache.hasMore !== false : true));
  const [fetching, setFetching] = useState(false);
  // A page-0 (re)load after a filter change: skeletons over the old list, so the page height
  // never collapses and the screen doesn't jump.
  const [refreshing, setRefreshing] = useState(!hasBackList);
  const sentinelRef = useRef(null);
  const requestRef = useRef(0);
  const inFlight = useRef(null);

  const filterKey = `${fundingFilter}|${category}|${selectedCities.slice().sort().join(',')}|${location?.lat ?? ''},${location?.lng ?? ''}|${selectedDays.slice().sort().join(',')}|${search}`;

  const fetchPage = useCallback(async (skipVal) => {
    const isFirst = skipVal === 0;
    // Observer callbacks can arrive before React paints fetching=true. Also discard a
    // superseded filter request even if its poster preparation finishes last.
    if (inFlight.current && (!isFirst || inFlight.current.key === filterKey)) return;
    const request = ++requestRef.current;
    inFlight.current = { request, key: filterKey };
    if (isFirst) setRefreshing(true); else setFetching(true);
    try {
      const r = await featuredWallService.getEvents({
        lat: location?.lat ?? null,
        lng: location?.lng ?? null,
        fundingType: fundingFilter,
        category: category || null,
        cities: selectedCities,
        days: selectedDays,
        search,
        skip: skipVal,
        take: PAGE,
      });
      if (request !== requestRef.current) return;
      if (r.success && r.data) {
        const batch = await prepareEventPosters(r.data.events || []);
        if (request !== requestRef.current) return;
        setFetched((prev) => {
          if (isFirst) return batch;
          const ids = new Set(prev.map(e => e.id));
          return [...prev, ...batch.filter(e => !ids.has(e.id))];
        });
        setSkip(skipVal + batch.length);
        setHasMore(!!r.data.hasMore);
      }
    } finally {
      if (request === requestRef.current) {
        inFlight.current = null; setRefreshing(false); setFetching(false);
      }
    }
  }, [location, fundingFilter, category, selectedCities, selectedDays, search, filterKey]);
  useEffect(() => () => { requestRef.current += 1; inFlight.current = null; }, []);

  // A mount that follows a back/forward already has the list from the cache — keep it. Any
  // other mount, or a later filter change, loads page 0.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!restoredRef.current) {
      restoredRef.current = true;
      if (fetched.length > 0) return;
    }
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    if (fetched.length === 0) return;
    saveWallList({ events: fetched, skip, hasMore });
  }, [fetched, skip, hasMore]);
  useEffect(() => { saveWallList({ search }); }, [search]);

  // Infinite scroll — the next page when the sentinel comes within 600px. Never a button.
  useEffect(() => {
    if (!sentinelRef.current) return undefined;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !fetching && !refreshing) fetchPage(skip);
    }, { rootMargin: '600px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, fetching, refreshing, skip, fetchPage]);

  // Keep the layout restorable: the poster ratios and the column each card went into, so a
  // back-nav lays every card out exactly where it was before the saved scroll is applied.
  const seed = hasBackList ? { ratios: backCache.ratios, masonry: backCache.masonry } : null;
  const onLayout = useCallback((ratios, masonry) => saveWallList({ ratios, masonry }), []);

  const count = fetched.length;
  const anyFilter = selectedDays.length || search || selectedCities.length || category || fundingFilter !== 'all';

  return (
    <section
      className="mx-auto max-w-[1280px]"
      style={{ padding: phone ? '28px 16px 110px' : '40px 28px 110px' }}
      aria-busy={loading || refreshing || undefined}
    >
      <div className="mb-6 flex items-center gap-5">
        <span className="pr-page-ink whitespace-nowrap font-mono font-bold tracking-[0.08em]" style={{ fontSize: phone ? 15 : 20 }}>
          ALL EVENTS ({count})
        </span>
        {!phone && <span className="pr-page-sub whitespace-nowrap font-mono text-[11px] tracking-[0.1em]">50-MI RADIUS</span>}
        <span className="pr-page-rule h-0.5 min-w-[12px] flex-1" />
        <EventViewSwitch value={viewMode} onChange={setViewMode} onDark />
      </div>

      <WallEventList
        events={fetched}
        viewMode={viewMode}
        resetKey={filterKey}
        refreshing={refreshing}
        fetching={fetching}
        onEventOpen={onEventOpen}
        portalFor={portalFor}
        seed={seed}
        onLayout={onLayout}
        empty={(
          <p className="pr-page-ink py-10 font-mono text-[13px] font-bold tracking-[0.16em]">
            {anyFilter ? 'NO EVENTS MATCH.' : 'NOTHING LIVE NEARBY YET.'}
          </p>
        )}
      />
      <div ref={sentinelRef} className="h-4" />
      {fetched.length > 0 && !fetching && !hasMore && !refreshing && (
        <div className="mt-8 flex h-[60px] items-center justify-center gap-3.5">
          <span className="pr-page-muted font-mono text-[12px] font-bold uppercase tracking-[0.18em]">End of list.</span>
          <span className="block h-2.5 w-2.5" style={{ background: '#E5E7EB' }} />
        </div>
      )}
    </section>
  );
}
